import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Grid, Chip, Divider, Table, TableHead,
  TableBody, TableRow, TableCell, Button, Select, MenuItem, FormControl, InputLabel,
  TableContainer, Alert, AlertTitle, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, TextField } from '@mui/material';
import { Block, RestoreFromTrash, Edit, Close } from '@mui/icons-material';
import FormRenderer from '../../components/FormRenderer';
import { applicationsAPI, formsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import dayjs from 'dayjs';

export default function ApplicationDetail() {
  var params = useParams(); var formId = params.id; var appId = params.appId;
  var sn = useSnackbar(); var notify = sn.enqueueSnackbar;
  var ds = useState(null); var data = ds[0]; var setData = ds[1];
  var fs = useState(null); var form = fs[0]; var setForm = fs[1];
  var es = useState(false); var editing = es[0]; var setEditing = es[1];
  var ess = useState(''); var editStatus = ess[0]; var setEditStatus = ess[1];
  var dg = useState(false); var discardOpen = dg[0]; var setDiscardOpen = dg[1];
  var dr = useState(''); var discardReason = dr[0]; var setDiscardReason = dr[1];
  var de = useState(''); var discardError = de[0]; var setDiscardError = de[1];
  var bs = useState(false); var busy = bs[0]; var setBusy = bs[1];
  var ea = useState(false); var editingAnswers = ea[0]; var setEditingAnswers = ea[1];
  var dr = useState({}); var draft = dr[0]; var setDraft = dr[1];
  var dn = useState(''); var draftName = dn[0]; var setDraftName = dn[1];
  var de2 = useState(''); var draftEmail = de2[0]; var setDraftEmail = de2[1];

  function reload() {
    return applicationsAPI.get(appId).then(function(r) {
      setData(r.data.data);
      setEditStatus(r.data.data.application.status);
    });
  }

  useEffect(function() {
    reload();
    formsAPI.get(formId).then(function(r) { setForm(r.data.data); });
  }, [appId, formId]);

  if (!data || !form) return React.createElement(Box, {sx:{p:4}}, React.createElement(Typography, null, 'Loading...'));

  var application = data.application;
  var reviews = data.reviews || [];
  var schema = typeof form.schema === 'string' ? JSON.parse(form.schema) : form.schema;
  if (typeof schema === 'string') { try { schema = JSON.parse(schema); } catch(e2) { schema = {}; } }
  var responses = typeof application.responses === 'string' ? JSON.parse(application.responses) : application.responses;

  function handleStatusUpdate() {
    applicationsAPI.update(appId, {status:editStatus}).then(function() {
      notify('Status updated', {variant:'success'});
      setEditing(false);
      reload();
    }).catch(function(e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'Update failed';
      notify(msg, {variant:'error'});
    });
  }

  function startEditing() {
    var r = typeof application.responses === 'string' ? JSON.parse(application.responses) : application.responses;
    setDraft(Object.assign({}, r || {}));
    setDraftName(application.applicant_name || '');
    setDraftEmail(application.applicant_email || '');
    setEditingAnswers(true);
  }

  function saveAnswers() {
    setBusy(true);
    applicationsAPI.update(appId, {
      responses: draft,
      applicant_name: draftName,
      applicant_email: draftEmail,
    }).then(function() {
      notify('Answers updated \u2014 the change is recorded in the audit log', {variant:'success'});
      setEditingAnswers(false);
      return reload();
    }).catch(function(e) {
      notify((e.response && e.response.data && e.response.data.error) || 'Could not save', {variant:'error'});
    }).then(function() { setBusy(false); });
  }

  function confirmDiscard() {
    setBusy(true); setDiscardError('');
    applicationsAPI.discard(appId, discardReason).then(function() {
      notify('Application discarded', {variant:'success'});
      setDiscardOpen(false);
      return reload();
    }).catch(function(e) {
      setDiscardError((e.response && e.response.data && e.response.data.error) || 'Could not discard');
    }).then(function() { setBusy(false); });
  }

  function handleRestore() {
    setBusy(true);
    applicationsAPI.restore(appId).then(function() {
      notify('Application restored', {variant:'success'});
      return reload();
    }).catch(function() {
      notify('Restore failed', {variant:'error'});
    }).then(function() { setBusy(false); });
  }

  // The score is computed and stored server-side when the review is saved, so
  // the weights and the configured formula are already applied. Re-summing the
  // raw values here would quietly disagree with every other view.
  function calcReviewerTotal(rv) {
    return (typeof rv.final_score === 'number') ? rv.final_score : null;
  }

  // With review stages each part of the application carries its own
  // recommendation; showing only the headline field hides the rest.
  function recommendationText(rv) {
    var recs = rv.stage_recommendations;
    if (typeof recs === 'string') { try { recs = JSON.parse(recs); } catch (e6) { recs = null; } }
    var keys = recs ? Object.keys(recs) : [];
    if (keys.length > 1) {
      return keys.map(function(k) { return String(recs[k]).replace('_', ' '); }).join(' / ');
    }
    return rv.recommendation ? rv.recommendation.replace('_', ' ') : '\u2014';
  }

  var isDiscarded = !!application.discarded_at;

  var E = React.createElement;
  return E(Box, null,
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:1, mb:2}},
      E(Typography, {variant:'h4'}, 'Application detail'),
      E(Box, {sx:{display:'flex', gap:1}},
        editingAnswers
          ? E(Button, {variant:'outlined', startIcon:E(Close), onClick:function(){ setEditingAnswers(false); }},
              'Cancel editing')
          : E(Button, {variant:'outlined', startIcon:E(Edit), disabled:busy, onClick:startEditing},
              'Edit answers'),
        isDiscarded
          ? E(Button, {variant:'outlined', startIcon:E(RestoreFromTrash), disabled:busy, onClick:handleRestore},
              'Restore to review')
          : E(Button, {variant:'outlined', color:'warning', startIcon:E(Block), disabled:busy,
              onClick:function(){ setDiscardReason(''); setDiscardError(''); setDiscardOpen(true); }},
              'Discard'))),

    isDiscarded ? E(Alert, {severity:'warning', sx:{mb:3}},
      E(AlertTitle, null, 'Discarded on ' + dayjs(application.discarded_at).format('MMM D, YYYY')),
      application.discard_reason
        ? 'Reason: ' + application.discard_reason
        : 'No reason was recorded.',
      ' Reviewers no longer see this application and it is excluded from assignment, decisions and the default export. Everything is still stored.') : null,

    E(Dialog, {open:discardOpen, onClose:function(){ setDiscardOpen(false); }, maxWidth:'sm', fullWidth:true},
      E(DialogTitle, null, 'Discard this application?'),
      E(DialogContent, null,
        E(DialogContentText, {sx:{mb:2}},
          'It comes out of the review pool and stops appearing for reviewers. The application, its files and any reviews stay in the database, and you can restore it at any time.'),
        E(DialogContentText, {variant:'body2', sx:{mb:2}},
          'An application that already has a submitted review cannot be discarded. Remove the reviewer\u2019s assignment first if you need to.'),
        E(TextField, {autoFocus:true, fullWidth:true, multiline:true, rows:2, label:'Reason (optional)',
          placeholder:'Duplicate submission, ineligible, test entry\u2026',
          value:discardReason, onChange:function(e){ setDiscardReason(e.target.value); }}),
        discardError ? E(Alert, {severity:'error', sx:{mt:2}}, discardError) : null),
      E(DialogActions, null,
        E(Button, {onClick:function(){ setDiscardOpen(false); }}, 'Cancel'),
        E(Button, {variant:'contained', color:'warning', disabled:busy, onClick:confirmDiscard}, 'Discard'))),

    E(Grid, {container:true, spacing:3},

      // Applicant info (main column)
      E(Grid, {item:true, xs:12, md:8},
        E(Card, null,
          E(CardContent, null,
            E(Box, {sx:{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2}},
              E(Typography, {variant:'h6'}, application.applicant_name),
              E(Chip, {label:(application.status || '').replace('_',' '), color:'primary'})),
            E(Typography, {variant:'body2',color:'text.secondary',sx:{mb:2}},
              application.applicant_email + ' \u2022 Submitted ' + dayjs(application.submitted_at).format('MMM D, YYYY HH:mm')),
            E(Divider, {sx:{mb:2}}),

            editingAnswers
              ? E(Box, null,
                  E(Alert, {severity:'warning', sx:{mb:2}},
                    'You are editing an applicant\u2019s own submission. Every change is written to the audit log with your name. Uploaded files cannot be changed here.'),
                  E(Grid, {container:true, spacing:2, sx:{mb:2}},
                    E(Grid, {item:true, xs:12, md:6},
                      E(TextField, {fullWidth:true, size:'small', label:'Applicant name', value:draftName,
                        onChange:function(e){ setDraftName(e.target.value); }})),
                    E(Grid, {item:true, xs:12, md:6},
                      E(TextField, {fullWidth:true, size:'small', label:'Applicant email', value:draftEmail,
                        onChange:function(e){ setDraftEmail(e.target.value); }}))),
                  E(FormRenderer, {schema:schema, values:draft, scholarOnly:true,
                    onChange:function(fieldId, value){
                      setDraft(function(prev){
                        var next = Object.assign({}, prev); next[fieldId] = value; return next;
                      });
                    }}),
                  E(Box, {sx:{display:'flex', gap:1, mt:2}},
                    E(Button, {variant:'contained', disabled:busy, onClick:saveAnswers}, 'Save answers'),
                    E(Button, {onClick:function(){ setEditingAnswers(false); }}, 'Cancel')))
              : (schema.fields || []).sort(function(a,b){return a.order-b.order;}).map(function(field) {
                  var val = responses[field.id];
                  var displayVal = Array.isArray(val) ? val.join(', ') : (val || '\u2014');
                  return E(Box, {key:field.id, sx:{mb:2}},
                    E(Typography, {variant:'subtitle2',color:'text.secondary',fontWeight:600}, field.label),
                    E(Typography, {variant:'body1',sx:{wordBreak:'break-word',whiteSpace:'pre-wrap',maxWidth:'100%',overflow:'hidden'}}, displayVal));
                })
          )
        )
      ),

      // Sidebar - scores + status + files
      E(Grid, {item:true, xs:12, md:4},
        // Reviewer scores summary (average + per-reviewer)
        (function() {
          var completed = reviews.filter(function(rv){return rv.status==='completed';});
          var scored = completed.length ? completed : reviews;
          var withScore = scored.filter(function(rv){ return calcReviewerTotal(rv) !== null; });
          var avg = null;
          if (withScore.length) {
            var sum = 0; withScore.forEach(function(rv){ sum += calcReviewerTotal(rv); });
            avg = sum / withScore.length;
          }
          return E(Card, {sx:{mb:2}},
            E(CardContent, null,
              E(Typography, {variant:'subtitle2',sx:{mb:1,fontWeight:600}}, 'Reviewer Scores'),
              reviews.length === 0
                ? E(Typography, {variant:'body2',color:'text.secondary'}, 'No scores yet')
                : E(Box, null,
                    E(Box, {sx:{display:'flex',alignItems:'center',gap:1,mb:1.5}},
                      E(Chip, {label:'Avg ' + (avg!=null?avg.toFixed(1):'\u2014'), color:'primary'}),
                      E(Typography, {variant:'caption',color:'text.secondary'},
                        'across ' + withScore.length + ' reviewer' + (withScore.length===1?'':'s'))),
                    reviews.map(function(rv) {
                      return E(Box, {key:rv.id, sx:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:1,mb:0.75}},
                        E(Box, {sx:{minWidth:0}},
                          E(Typography, {variant:'body2',noWrap:true}, rv.reviewer_name),
                          E(Typography, {variant:'caption',color:'text.secondary'}, recommendationText(rv))),
                        E(Chip, {label: calcReviewerTotal(rv) === null ? '\u2014'
                            : Number(calcReviewerTotal(rv)).toFixed(2),
                          size:'small', variant:'outlined',
                          color: rv.status==='completed'?'primary':'default'}));
                    }))));
        })(),

        E(Card, {sx:{mb:2}},
          E(CardContent, null,
            E(Typography, {variant:'subtitle2',sx:{mb:1,fontWeight:600}}, 'Update Status'),
            E(Typography, {variant:'caption',color:'text.secondary',sx:{display:'block',mb:1}}, 'This is the admin decision status, independent of reviewer recommendations.'),
            E(FormControl, {fullWidth:true, size:'small', sx:{mb:1}},
              E(Select, {value:editStatus, onChange:function(e){setEditStatus(e.target.value);setEditing(true);}},
                ['submitted','under_review','reviewed','accepted','rejected','waitlisted'].map(function(s) {
                  return E(MenuItem, {key:s, value:s}, s.replace('_',' '));
                }))),
            editing && E(Button, {size:'small',variant:'contained',onClick:handleStatusUpdate}, 'Save Status')
          )
        ),

        // Files
        application.files && (function() {
          try {
            var files = typeof application.files === 'string' ? JSON.parse(application.files) : application.files;
            if (files && files.length > 0) {
              return E(Card, {sx:{mb:2}},
                E(CardContent, null,
                  E(Typography, {variant:'subtitle2',sx:{mb:1,fontWeight:600}}, 'Uploaded Files'),
                  files.map(function(f, i) {
                    return E(Button, {key:i, size:'small', href:'/api/files/'+f.stored_name, target:'_blank', sx:{display:'block',mb:0.5}}, f.filename);
                  })
                )
              );
            }
          } catch(e3) {}
          return null;
        })()
      ),

      // Reviews section (full width below)
      E(Grid, {item:true, xs:12},
        E(Card, null,
          E(CardContent, null,
            E(Typography, {variant:'h6',sx:{mb:2}}, 'Reviews (' + reviews.length + ')'),
            reviews.length === 0
              ? E(Typography, {color:'text.secondary'}, 'No reviews yet')
              : E(TableContainer, null,
                  E(Table, {size:'small'},
                    E(TableHead, null,
                      E(TableRow, null,
                        E(TableCell, null, 'Reviewer'),
                        E(TableCell, null, 'Status'),
                        E(TableCell, null, 'Recommendation'),
                        E(TableCell, null, 'Total Score'),
                        E(TableCell, null, 'Comment'),
                        E(TableCell, null, 'Submitted'))),
                    E(TableBody, null,
                      reviews.map(function(rv) {
                        var total = calcReviewerTotal(rv);
                        return E(TableRow, {key:rv.id},
                          E(TableCell, null, rv.reviewer_name),
                          E(TableCell, null, E(Chip, {label:rv.status, size:'small', color:rv.status==='completed'?'success':'warning'})),
                          E(TableCell, null, recommendationText(rv)),
                          E(TableCell, null, E(Chip, {
                            label: total === null ? '\u2014' : Number(total).toFixed(2),
                            size:'small', color:'primary', variant:'outlined'})),
                          E(TableCell, {sx:{maxWidth:250,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}, rv.overall_comment || '\u2014'),
                          E(TableCell, null, rv.submitted_at ? dayjs(rv.submitted_at).format('MMM D HH:mm') : '\u2014'));
                      }))))
          )
        )
      )
    )
  );
}
